import * as PIXI from 'pixi.js';
import { createAtom } from '../reactivity';
import type {
    VideoFrameSource,
    VideoFramesProvider,
    VideoFramesProviderEvent,
    VideoFramesProviderFactory,
    VideoFramesProviderListener,
    VideoFramesProviderListenerOptions,
    VideoFramesProviderParams,
    VideoReadyState,
} from './VideoFramesProvider';

const DEBUG_NAME_ATTRIBUTE = 'data-name-for-debugging';

const METADATA_EVENTS = ['loadedmetadata', 'durationchange', 'resize', 'emptied'] as const;

const FRAME_PRESENTATION_DEADLINE_MS = 1000;

type PendingSeek = {
    elementSeeked: boolean;
    framePresented: boolean;
    frameDeadline: ReturnType<typeof setTimeout> | null;
};

/**
 * The default {@link VideoFramesProvider}: a `<video>` element decoded by the browser. This is the
 * only place in luminary that is allowed to talk to the `HTMLVideoElement` interface — everything
 * else goes through {@link VideoFramesProvider}.
 *
 * NOTE: DO NOT mute before load, as Chrome bugs and creates a WebGL error
 *       https://bugs.chromium.org/p/chromium/issues/detail?id=898550
 *
 * Preloading:
 * The only reason we create the video element ourselves is to avoid PIXI setting preload="auto".
 * Preloading causes seeking to be super slow for some videos.
 * It requires more network and processing, especially with trimmed media, where
 * it seems that browsers try to load all the file instead of seeking.
 */
export class HtmlVideoFramesProvider implements VideoFramesProvider {
    #videoElement: HTMLVideoElement;
    #sourceUrl: string;
    #mimeType: string;

    #metadataSignal = createAtom('HtmlVideoFramesProvider.metadata');

    #seekAwaitingFrame: PendingSeek | null = null;
    #presentedSeekTime: number | null = null;
    #seekedEvents = new EventTarget();

    constructor({ sourceUrl, mimeType }: VideoFramesProviderParams) {
        this.#sourceUrl = sourceUrl;
        this.#mimeType = mimeType;

        this.#videoElement = (() => {
            const videoElement = document.createElement('video');
            videoElement.setAttribute('preload', 'none');
            videoElement.setAttribute('webkit-playsinline', '');
            videoElement.setAttribute('playsinline', '');

            PIXI.BaseImageResource.crossOrigin(videoElement, sourceUrl);

            const sourceElement = document.createElement('source');
            sourceElement.src = sourceUrl;
            sourceElement.type = mimeType;
            videoElement.appendChild(sourceElement);

            return videoElement;
        })();
        METADATA_EVENTS.forEach((event) => {
            this.#videoElement.addEventListener(event, () => this.#metadataSignal.reportChanged());
        });
        this.#videoElement.addEventListener('seeked', this.#onElementSeeked);
        document.addEventListener('visibilitychange', this.#onVisibilityChange);
        this.debugName = 'original';
    }

    #onVisibilityChange = () => {
        const seek = this.#seekAwaitingFrame;
        if (!seek || !document.hidden) {
            return;
        }
        seek.framePresented = true;
        this.#finishSeekIfFramePresented(seek);
    };

    #onElementSeeked = () => {
        const seek = this.#seekAwaitingFrame;
        if (!seek) {
            this.#seekedEvents.dispatchEvent(new Event('seeked'));
            return;
        }
        seek.elementSeeked = true;
        this.#finishSeekIfFramePresented(seek);
        if (seek === this.#seekAwaitingFrame) {
            seek.frameDeadline = setTimeout(() => {
                seek.framePresented = true;
                this.#finishSeekIfFramePresented(seek);
            }, FRAME_PRESENTATION_DEADLINE_MS);
        }
    };

    #finishSeekIfFramePresented(seek: PendingSeek) {
        if (seek !== this.#seekAwaitingFrame) {
            return;
        }
        if (!seek.elementSeeked || !seek.framePresented) {
            return;
        }
        if (seek.frameDeadline) {
            clearTimeout(seek.frameDeadline);
        }
        this.#seekAwaitingFrame = null;
        this.#seekedEvents.dispatchEvent(new Event('seeked'));
    }

    get currentFrame(): VideoFrameSource | null {
        return this.#videoElement;
    }

    get videoWidth(): number {
        this.#metadataSignal.reportObserved();
        return this.#videoElement.videoWidth;
    }

    get videoHeight(): number {
        this.#metadataSignal.reportObserved();
        return this.#videoElement.videoHeight;
    }

    get duration(): number | null {
        this.#metadataSignal.reportObserved();
        const duration = this.#videoElement.duration;
        return Number.isNaN(duration) ? null : duration;
    }

    get isLoaded(): boolean {
        return this.duration !== null;
    }

    get readyState(): VideoReadyState {
        return this.#videoElement.readyState as VideoReadyState;
    }

    get seeking(): boolean {
        return this.#videoElement.seeking || this.#seekAwaitingFrame !== null;
    }

    get paused(): boolean {
        return this.#videoElement.paused;
    }

    get sourceUrl(): string {
        return this.#sourceUrl;
    }

    get audioElement(): HTMLMediaElement | null {
        return this.#videoElement;
    }

    get currentTime(): number {
        return this.#videoElement.currentTime;
    }

    /**
     * The element fires `seeked` and reports `HAVE_ENOUGH_DATA` before the seeked frame can be
     * read, and drawing it in that window yields a transparent frame. The seek is held open until
     * the frame has been presented. A hidden document presents no frames, so there it is not held.
     */
    set currentTime(time: number) {
        if (document.hidden) {
            this.#seekAwaitingFrame = null;
            this.#presentedSeekTime = null;
            this.#videoElement.currentTime = time;
            return;
        }
        if (time === this.#presentedSeekTime && time === this.#videoElement.currentTime) {
            this.#videoElement.currentTime = time;
            return;
        }
        if (this.#seekAwaitingFrame?.frameDeadline) {
            clearTimeout(this.#seekAwaitingFrame.frameDeadline);
        }
        const seek: PendingSeek = {
            elementSeeked: false,
            framePresented: false,
            frameDeadline: null,
        };
        this.#seekAwaitingFrame = seek;
        this.#presentedSeekTime = null;
        this.#videoElement.currentTime = time;
        this.#videoElement.requestVideoFrameCallback(() => {
            seek.framePresented = true;
            if (seek === this.#seekAwaitingFrame) {
                this.#presentedSeekTime = time;
            }
            this.#finishSeekIfFramePresented(seek);
        });
    }

    get playbackRate(): number {
        return this.#videoElement.playbackRate;
    }

    set playbackRate(rate: number) {
        this.#videoElement.playbackRate = rate;
    }

    get muted(): boolean {
        return this.#videoElement.muted;
    }

    set muted(muted: boolean) {
        this.#videoElement.muted = muted;
    }

    get debugName(): string {
        return this.#videoElement.getAttribute(DEBUG_NAME_ATTRIBUTE) ?? '';
    }

    set debugName(name: string) {
        this.#videoElement.setAttribute(DEBUG_NAME_ATTRIBUTE, name);
    }

    load(): void {
        this.#videoElement.load();
    }

    async play(): Promise<void> {
        return this.#videoElement.play();
    }

    pause(): void {
        this.#videoElement.pause();
    }

    clone(): VideoFramesProvider {
        const clone = new HtmlVideoFramesProvider({
            sourceUrl: this.#sourceUrl,
            mimeType: this.#mimeType,
        });
        clone.load();
        return clone;
    }

    destroy(): void {
        document.removeEventListener('visibilitychange', this.#onVisibilityChange);
        if (this.#seekAwaitingFrame?.frameDeadline) {
            clearTimeout(this.#seekAwaitingFrame.frameDeadline);
        }
        this.#seekAwaitingFrame = null;
        this.#videoElement.pause();
        this.#videoElement.src = '';
        this.#videoElement.load();
    }

    /**
     * Listeners are registered in the capture phase because a failing `<source>` fires its `error`
     * event at the child element and media events do not bubble; without capture a load failure
     * would never reach the listener.
     */
    addEventListener(
        type: VideoFramesProviderEvent,
        listener: VideoFramesProviderListener,
        options?: VideoFramesProviderListenerOptions,
    ): void {
        if (type === 'seeked') {
            this.#seekedEvents.addEventListener(type, listener, options);
            return;
        }
        this.#videoElement.addEventListener(type, listener, {
            ...options,
            capture: true,
        });
    }

    removeEventListener(
        type: VideoFramesProviderEvent,
        listener: VideoFramesProviderListener,
    ): void {
        if (type === 'seeked') {
            this.#seekedEvents.removeEventListener(type, listener);
            return;
        }
        this.#videoElement.removeEventListener(type, listener, { capture: true });
    }
}

export const createHtmlVideoFramesProvider: VideoFramesProviderFactory = (params) =>
    new HtmlVideoFramesProvider(params);
